import { Link as RouterLink } from 'react-router-dom';
import { Link } from './sidebar-config';

interface SidebarLinkProps {
  link: Link;
  isActive: boolean;
  onNavigate?: () => void;
  iconColor?: string;
  hasAccess?: boolean; // Default true for backward compatibility
  isFavorited?: boolean; // Show star indicator when favorited
  badgeCount?: number; // Red count bubble after the label (hidden when 0)
}

export const SidebarLink: React.FC<SidebarLinkProps> = ({
  link,
  isActive,
  onNavigate,
  iconColor = 'text-accent-light',
  hasAccess = true,
  isFavorited = false,
  badgeCount = 0,
}) => {
  const handleClick = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  // Placeholder links (coming soon)
  if (link.isPlaceholder) {
    return (
      <li>
        <div className="flex items-center p-3 rounded-lg text-ink-muted cursor-not-allowed opacity-60 relative">
          <i className={`fa-solid ${link.icon} w-6 text-center mr-3`}></i>
          <span className="italic text-sm">{link.label}</span>
          <span className="ml-auto text-xs">(Coming Soon)</span>
          {link.isNew && (
            <span className="absolute -top-1.5 -right-1 px-1.5 py-0.5 text-[10px] font-bold border border-success text-success bg-surface rounded uppercase pointer-events-none shadow-sm z-20">
              New
            </span>
          )}
        </div>
      </li>
    );
  }

  // No access - disabled link with tooltip
  if (!hasAccess) {
    return (
      <li>
        <span
          className="flex items-center p-3 rounded-lg cursor-not-allowed opacity-50 text-ink-faint relative"
          title="You don't have permission to access this page"
        >
          <i className={`fa-solid ${link.icon} w-6 text-center mr-3`}></i>
          <span>{link.label}</span>
          {link.isNew && (
            <span className="absolute -top-1.5 -right-1 px-1.5 py-0.5 text-[10px] font-bold border border-success text-success bg-surface rounded uppercase pointer-events-none shadow-sm z-20">
              New
            </span>
          )}
        </span>
      </li>
    );
  }

  // External links - use regular <a> tag
  if (link.isExternal) {
    return (
      <li>
        <a
          href={link.href}
          onClick={handleClick}
          className={`flex items-center p-3 rounded-lg transition-colors ${
            isActive ? 'bg-chrome text-ink-chrome' : 'hover:bg-chrome'
          } relative`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <i
            className={`fa-solid ${link.icon} w-6 text-center mr-3 ${
              isActive ? iconColor : ''
            }`}
          ></i>
          <span className="flex-1">{link.label}</span>
          <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-40 ml-1" />
          {link.isNew && (
            <span className="absolute -top-1.5 -right-1 px-1.5 py-0.5 text-[10px] font-bold border border-success text-success bg-surface rounded uppercase pointer-events-none shadow-sm z-20">
              New
            </span>
          )}
        </a>
      </li>
    );
  }

  // Internal links - use React Router Link for instant navigation
  return (
    <li>
      <RouterLink
        to={link.href}
        onClick={handleClick}
        className={`flex items-center p-3 rounded-lg transition-colors ${
          isActive ? 'bg-chrome text-ink-chrome' : 'hover:bg-chrome'
        } relative`}
      >
        <i
          className={`fa-solid ${link.icon} w-6 text-center mr-3 ${
            isActive ? iconColor : ''
          }`}
        ></i>
        <span className="flex-1">
          {link.label}
          {/* Count bubble (e.g. open quizzes) right after the label - same look as the notification bell's unread badge */}
          {badgeCount > 0 && (
            <>
              <span
                aria-hidden="true"
                className="ml-2 inline-flex items-center justify-center align-middle bg-danger text-ink-inverse text-xs font-bold rounded-full h-5 min-w-[1.25rem] px-1"
              >
                {badgeCount > 9 ? '9+' : badgeCount}
              </span>
              <span className="sr-only">{`, ${badgeCount} open`}</span>
            </>
          )}
        </span>
        {/* NEW badge for recently added pages */}
        {link.isNew && (
          <span className="absolute -top-1.5 -right-1 px-1.5 py-0.5 text-[10px] font-bold border border-success text-success bg-surface rounded uppercase pointer-events-none shadow-sm z-20">
            New
          </span>
        )}
        {/* Favorite star indicator */}
        {isFavorited && (
          <i className="fa-solid fa-star text-favorite text-xs ml-2" title="Favorited" />
        )}
      </RouterLink>
    </li>
  );
};
